import { useGame } from "@/contexts/GameContext";
import { getSmartRandomQuestion } from "@/lib/utils";
import { modes } from "@/lib/constants";
import BottleSpinScreen from "@/components/screens/BottleSpinScreen";
import QuestionScreen from "@/components/screens/QuestionScreen";
import TaskTypeSelection from "@/components/screens/TaskTypeSelection";
import { Button } from "@/components/ui/button";

import { AnimatePresence, motion } from "framer-motion";
import WhoIsMostLikelyScreen from "./WhoIsMostLikelyScreen";
import NeverHaveIEverScreen from "./NeverHaveIEverScreen";

export default function GameScreen() {
  const { state, dispatch } = useGame();

  const currentPlayer = state.players[state.currentPlayerIndex];

  // Jeśli nie ma graczy (np. po resecie), wróć do startu
  if (!currentPlayer) {
    dispatch({ type: "SET_SCREEN", payload: "start" });
    return null; // Zwróć null, aby uniknąć błędów renderowania
  }

  console.log(
    "🎮 GameScreen - Renderowanie z graczem:",
    currentPlayer.name,
    "Tryb:",
    state.settings.gameMode
  );

  const handlePlayerSelected = (playerIndex) => {
    console.log(
      "👆 Wybrano gracza:",
      playerIndex,
      state.players[playerIndex]?.name
    );
    dispatch({ type: "SET_SELECTED_PLAYER", payload: playerIndex });

    const currentModeConfig = modes.find(
      (m) => m.id === state.settings.gameMode
    );

    // 4. Jeśli tryb to "classic", pokaż wybór P/W, inaczej losuj od razu
    if (currentModeConfig?.id === "classic") {
      dispatch({ type: "SHOW_TASK_TYPE_SELECTION", payload: true });
    } else {
      // Dla innych trybów losujemy od razu (bez wyboru P/W)
      handleDrawTask();
    }
  };

  // 5. Przepisana funkcja losowania
  const handleDrawTask = async (forcedType = null, showDialog = true) => {
    console.log(
      "🎲 Rozpoczynanie losowania, wymuszony typ:",
      forcedType,
      "Pokaż dialog:",
      showDialog
    );
    const { settings, allQuestions, drawnIds } = state;

    const currentModeConfig = modes.find((m) => m.id === settings.gameMode);

    if (!currentModeConfig) {
      console.error("Nie można znaleźć konfiguracji trybu:", settings.gameMode);
      alert("Błąd: Nie można znaleźć konfiguracji wybranego trybu gry.");
      return;
    }

    // Ustawienia dla funkcji losującej
    const drawSettings = {
      types: forcedType ? [forcedType] : currentModeConfig.types,
      nsfw: settings.nsfwEnabled,
      difficulty: settings.difficulty,
      occasions: settings.occasions,
      themes: settings.themes,
    };

    console.log("⚙️ Ustawienia losowania:", drawSettings);
    console.log("🧠 Wylosowane ID:", Array.from(drawnIds));

    // Wczytujemy pytania ze stanu
    let selected = getSmartRandomQuestion(allQuestions, drawSettings, drawnIds);

    // Sprawdzamy czy pula się skończyła I CZY SĄ JAKIEŚ PYTANIA PASUJĄCE OGÓLNIE
    const allMatching = getSmartRandomQuestion(
      allQuestions,
      drawSettings,
      new Set()
    );

    if (selected && drawnIds.has(selected.id) && allMatching) {
      console.warn(
        "Pula wyczerpana, resetuję 'drawnIds' i losuję jeszcze raz."
      );
      dispatch({ type: "CLEAR_DRAWN_IDS" });
      const newDrawnIds = new Set();
      selected = getSmartRandomQuestion(
        allQuestions,
        drawSettings,
        newDrawnIds
      );
      if (selected) {
        // Jeśli wylosowano, od razu dodajemy ID do nowego setu
        newDrawnIds.add(selected.id);
        dispatch({ type: "SET_DRAWN_IDS", payload: newDrawnIds }); // <<< Musisz mieć tę akcję w reducerze!
      } else {
        // Jeśli nawet po resecie nic nie ma (nie powinno się zdarzyć, jeśli allMatching jest true)
        console.error(
          "❌ Błąd: Nie udało się wylosować nawet po resecie puli!"
        );
        alert("Coś poszło nie tak z losowaniem!");
        return;
      }
    } else if (!selected && drawnIds.size > 0 && allMatching) {
      // Dodatkowa obsługa: jeśli `getSmartRandomQuestion` zwróciło null, bo nie ma *nowych*,
      // ale są *jakiekolwiek* pasujące, to też resetujemy.
      console.warn(
        "Pula wyczerpana (brak nowych), resetuję 'drawnIds' i losuję jeszcze raz."
      );
      dispatch({ type: "CLEAR_DRAWN_IDS" });
      const newDrawnIds = new Set();
      selected = getSmartRandomQuestion(
        allQuestions,
        drawSettings,
        newDrawnIds
      );
      if (selected) {
        newDrawnIds.add(selected.id);
        dispatch({ type: "SET_DRAWN_IDS", payload: newDrawnIds });
      } else {
        console.error(
          "❌ Błąd: Nie udało się wylosować nawet po resecie puli!"
        );
        alert("Coś poszło nie tak z losowaniem!");
        return;
      }
    }

    if (selected) {
      console.log("✅ Wylosowano:", selected.id, selected.text);
      dispatch({ type: "SET_CURRENT_QUESTION", payload: selected });
      if (!drawnIds.has(selected.id)) {
        // Dodaj tylko jeśli nie było (ważne po resecie)
        dispatch({ type: "ADD_DRAWN_ID", payload: selected.id });
      }
      dispatch({ type: "SHOW_TASK_TYPE_SELECTION", payload: false });
      // Pokaż dialog tylko jeśli zażądano
      if (showDialog) {
        dispatch({ type: "SHOW_QUESTION_DIALOG", payload: true });
      }
    } else {
      console.error("❌ Nie znaleziono pasującego pytania!");
      alert("Ups! Nie znaleziono pasujących pytań dla tych ustawień.");
      dispatch({ type: "SHOW_TASK_TYPE_SELECTION", payload: false });
    }
  };

  // Zmieniamy nazwę oryginalnej funkcji, aby uniknąć konfliktu
  const _selectTaskType = (type) => {
    handleDrawTask(type, true); // Dla P/W chcemy dialog
  };

  const skipTurnAndSelectTask = () => {
    dispatch({ type: "NEXT_TURN" }); // Użyj NEXT_TURN lub SKIP_TURN
    dispatch({ type: "SHOW_TASK_TYPE_SELECTION", payload: false });
  };

  // 7. Funkcja do renderowania głównego obszaru gry
  const renderGameArea = () => {
    // Jeśli mamy wybrane pytanie, nie pokazuj nic (bo jest dialog)
    if (state.showQuestionDialog && state.currentQuestion) {
      return <QuestionScreen />;
    }
    // Jeśli mamy wybrać typ P/W
    if (state.showTaskTypeSelection) {
      return (
        <motion.div className="w-full" key="taskSelection">
          <TaskTypeSelection
            onSelect={_selectTaskType}
            onSkip={skipTurnAndSelectTask}
          />
        </motion.div>
      );
    }

    // W zależności od trybu, pokaż butelkę lub przycisk losowania
    switch (state.settings.gameMode) {
      case "classic":
        return (
          <motion.div key="bottleSpin">
            <BottleSpinScreen onPlayerSelected={handlePlayerSelected} />
          </motion.div>
        );
      case "who_is_most_likely":
        return <WhoIsMostLikelyScreen handleDrawTask={handleDrawTask} />;
      case "never_have_i_ever":
        return <NeverHaveIEverScreen handleDrawTask={handleDrawTask} />;
      default:
        return (
          <motion.div key="default">
            <p>Wybierz akcję dla trybu: {state.settings.gameMode}</p>
            <Button onClick={() => handleDrawTask()}>Losuj</Button>
          </motion.div>
        );
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center relative z-10">
      <AnimatePresence mode="wait">
        {renderGameArea()} {/* Używamy nowej funkcji do renderowania */}
      </AnimatePresence>
    </div>
  );
}
